/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@/shared/ui/radix-icons';
import Text from '../../basic/Text';
import UnstyledButton from '../../basic/UnstyledButton';
import {
  monthCells,
  fromISODate,
  isOutOfRange,
  shiftMonth,
  toISODate,
} from './date-grid';

export type DatePickerPopoverProps = {
  /** Currently selected date, ISO `yyyy-MM-dd`. */
  value?: string;
  /** Inclusive lower bound, ISO. */
  min?: string;
  /** Inclusive upper bound, ISO. */
  max?: string;
  /** Called with the picked ISO date. */
  onSelect: (iso: string) => void;
  /** Called when the popover should close without a pick. */
  onClose: () => void;
};

/** Sunday-first single-letter weekday labels, matching the agenda's grid. */
const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;

const DAY_BASE =
  'flex aspect-square h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--seed-base) focus-visible:ring-offset-1';

const NAV_BUTTON =
  'flex h-7 w-7 items-center justify-center rounded-(--radius-md) text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-1';

/**
 * Visual treatment for a day cell, keeping the three states apart: filled
 * selection, outlined today, and the two combined.
 * @param selected - Whether this is the selected date.
 * @param today - Whether this is the current day.
 * @param disabled - Whether the date is out of range.
 * @returns Tailwind classes for the cell.
 */
const dayStateClass = (
  selected: boolean,
  today: boolean,
  disabled: boolean,
): string => {
  if (disabled) return 'text-[var(--text-tertiary)] opacity-40 cursor-not-allowed';
  if (selected) return 'bg-(--seed-base) font-bold text-(--text-inverse)';
  if (today) return 'ring-1 ring-(--seed-base) font-bold text-(--seed-base) hover:bg-(--surface-secondary)';
  return 'text-[var(--text-primary)] hover:bg-[var(--surface-secondary)]';
};

/**
 * Month calendar in a popover, for choosing a date without the browser's own
 * picker — which renders in the platform's colours and cannot be styled, so it
 * sat outside the design system wherever a date field appeared.
 *
 * Closes on Escape and on a click outside, and returns focus handling to the
 * caller, which owns the trigger.
 *
 * @param props - Selected value, bounds and callbacks.
 * @returns The rendered popover.
 */
export const DatePickerPopover: React.FC<DatePickerPopoverProps> = ({
  value,
  min,
  max,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedDate = fromISODate(value);
  const [view, setView] = useState(() => {
    const base = selectedDate ?? new Date();
    return { year: base.getFullYear(), month: base.getMonth() };
  });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    const onPointerDown = (e: MouseEvent): void => {
      if (!containerRef.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  const todayISO = toISODate(new Date());
  const cells = monthCells(view.year, view.month);
  const monthLabel = new Date(view.year, view.month, 1).toLocaleDateString(
    undefined,
    { month: 'long', year: 'numeric' },
  );

  return (
    <div
      ref={containerRef}
      data-testid="date-picker-popover"
      role="dialog"
      aria-label={t('date_picker.label', 'Escolher data')}
      className="absolute right-0 top-full z-50 mt-(--space-1) w-[17rem] rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-(--space-3) shadow-[var(--shadow-md)]"
    >
      <div className="mb-(--space-2) grid grid-cols-7 items-center">
        <UnstyledButton
          type="button"
          aria-label={t('date_picker.previous_month', 'Mês anterior')}
          onClick={() => setView(shiftMonth(view.year, view.month, -1))}
          className={NAV_BUTTON}
        >
          <ChevronLeftIcon />
        </UnstyledButton>
        <Text
          component="span"
          className="col-span-5 text-center text-(--text-small) font-bold capitalize text-[var(--text-primary)]"
        >
          {monthLabel}
        </Text>
        <UnstyledButton
          type="button"
          aria-label={t('date_picker.next_month', 'Próximo mês')}
          onClick={() => setView(shiftMonth(view.year, view.month, 1))}
          className={`${NAV_BUTTON} justify-self-end`}
        >
          <ChevronRightIcon />
        </UnstyledButton>
      </div>

      <div className="grid grid-cols-7 gap-y-(--space-1)">
        {WEEKDAYS.map((label, i) => (
          <Text
            key={`${label}-${i}`}
            component="span"
            aria-hidden="true"
            className="flex h-6 w-full items-center justify-center text-xs font-semibold uppercase text-[var(--text-tertiary)]"
          >
            {label}
          </Text>
        ))}
        {cells.map((cell, i) => {
          if (!cell) {
            return (
              <Text
                key={`blank-${i}`}
                component="span"
                aria-hidden="true"
                className="block h-7 w-full"
              />
            );
          }
          const disabled = isOutOfRange(cell.iso, min, max);
          return (
            <UnstyledButton
              key={cell.iso}
              type="button"
              data-testid="date-picker-day"
              aria-pressed={cell.iso === value}
              disabled={disabled}
              onClick={() => onSelect(cell.iso)}
              className="flex w-full items-center justify-center"
            >
              <Text
                component="span"
                className={`${DAY_BASE} ${dayStateClass(cell.iso === value, cell.iso === todayISO, disabled)}`}
              >
                {cell.day}
              </Text>
            </UnstyledButton>
          );
        })}
      </div>

      <div className="mt-(--space-2) flex justify-between border-t border-[var(--border-ultra-subtle)] pt-(--space-2)">
        <UnstyledButton
          type="button"
          onClick={() => onSelect('')}
          className="rounded-(--radius-md) px-(--space-2) py-(--space-1) text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-secondary)]"
        >
          {t('date_picker.clear', 'Limpar')}
        </UnstyledButton>
        <UnstyledButton
          type="button"
          disabled={isOutOfRange(todayISO, min, max)}
          onClick={() => onSelect(todayISO)}
          className="rounded-(--radius-md) px-(--space-2) py-(--space-1) text-xs font-semibold text-[var(--seed-base)] transition-colors hover:bg-[var(--surface-secondary)] disabled:opacity-40"
        >
          {t('date_picker.today', 'Hoje')}
        </UnstyledButton>
      </div>
    </div>
  );
};

export default DatePickerPopover;
